// The Claude button (decision 137): a link like the others in the sidebar (a tab on a phone), push to talk, nothing
// to type and nothing floating. Tap, say it, and Claude answers in a line under the button, or opens the invoice or
// count screen filled in for you to check and save.
import type { AssistantAction } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { confirmLeaving } from '../hooks';
import { useLocale } from '../i18n';
import { Diamond } from './brand';

type Turn = { role: 'user' | 'assistant'; text: string };
/** Turns kept so "and two more limes" is understood; older ones are let go. */
const KEEP_TURNS = 8;
/** Only the person's second tap sends (JL: "it cuts me off"); past this it sends what it has. */
const MAX_LISTEN_MS = 120_000;
/** The browser ending this many times in a row with nothing heard is a phone that will not listen again. */
const EMPTY_ENDS = 3;
/** After the second tap, a phone that never says it stopped still sends after this. */
const STOP_FALLBACK_MS = 1500;
/** The server takes turns up to 4,000 characters; a long answer is cut so it never blocks the next question. */
const MAX_TURN_CHARS = 3900;

/** Where a filled screen goes: the page reads it from the navigation state. */
export const ASSIST_STATE = 'assist';
export type AssistState = { [ASSIST_STATE]: AssistantAction };

interface SpeechResultEvent {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
}
interface Recognizer {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: SpeechResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start(): void;
  stop(): void;
}
function makeRecognizer(): Recognizer | null {
  const w = window as unknown as { SpeechRecognition?: new () => Recognizer; webkitSpeechRecognition?: new () => Recognizer };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

export function ClaudeButton({ place }: { place: 'side' | 'tab' }) {
  const { me } = useAuth();
  const allowed = me?.role === 'owner' || me?.role === 'office';
  const status = useQuery({ queryKey: ['assistant-status'], queryFn: ({ signal }) => api.assistantStatus(signal), enabled: allowed, staleTime: 10 * 60_000 });
  // push to talk: a browser that cannot listen gets no button
  if (!allowed || !status.data?.configured || !makeRecognizerAvailable()) return null;
  return <ClaudeSlot place={place} />;
}

function ClaudeSlot({ place }: { place: 'side' | 'tab' }) {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [phase, setPhase] = useState<'idle' | 'listening' | 'paused' | 'busy'>('idle');
  const [text, setText] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [used, setUsed] = useState<string[]>([]);
  /** what Claude was last sent, kept on screen so the person sees what it heard */
  const [lastHeard, setLastHeard] = useState('');
  const [problem, setProblem] = useState<'noSpeech' | 'micBlocked' | null>(null);
  /** the line under the link; closed with ✕, and on a phone after Claude opened a filled screen */
  const [shown, setShown] = useState(true);
  const recognizer = useRef<Recognizer | null>(null);
  const heard = useRef('');
  const session = useRef<Session | null>(null);
  const turnsRef = useRef<Turn[]>([]);
  turnsRef.current = turns;
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  // signing out (the only time the link goes away) ends the listening without sending
  const unmounted = useRef(false);
  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      const s = session.current;
      if (s) end(s);
      try {
        recognizer.current?.stop();
      } catch {
        // already stopped
      }
    };
  }, []);

  async function ask(said: string) {
    const words = said.trim().slice(0, MAX_TURN_CHARS);
    if (!words) return;
    setLastHeard(words);
    const before = turnsRef.current;
    const history: Turn[] = [...before, { role: 'user' as const, text: words }].slice(-KEEP_TURNS);
    setTurns(history);
    setText('');
    setPhase('busy');
    setShown(true);
    setError(null);
    setUsed([]);
    try {
      const answer = await api.askClaude({ messages: history.map((x) => ({ ...x, text: x.text.slice(0, MAX_TURN_CHARS) })), screen: pathRef.current });
      if (unmounted.current) return;
      setTurns((cur) => [...cur, { role: 'assistant' as const, text: answer.reply.slice(0, MAX_TURN_CHARS) }].slice(-KEEP_TURNS));
      setUsed(answer.used);
      if (answer.action) {
        const state: AssistState = { [ASSIST_STATE]: answer.action };
        const to = answer.action.kind === 'invoice' ? '/invoices/new' : '/stock';
        // the same screen takes the filled work in beside what is typed; another screen would drop it: ask first
        if (pathRef.current !== to && !confirmLeaving()) return;
        navigate(to, { state });
        // on a phone the filled screen speaks for itself; the answer would sit on its Save bar
        if (place === 'tab') setShown(false);
      }
    } catch (e) {
      // a request that failed is not carried into the next one
      setTurns(before);
      setError(e);
    } finally {
      if (!unmounted.current) setPhase('idle');
    }
  }

  /** Stop everything for this listening; never sends. */
  function end(s: Session) {
    s.stopping = true;
    s.sent = true;
    if (s.timer) clearTimeout(s.timer);
    if (s.fallback) clearTimeout(s.fallback);
    if (session.current === s) session.current = null;
  }

  /** Send what was heard, once: the person's tap (or the two-minute net) is the only way here. */
  function send(s: Session) {
    if (s.sent || unmounted.current) return;
    end(s);
    try {
      recognizer.current?.stop();
    } catch {
      // already stopped
    }
    const words = heard.current.trim();
    if (words) void ask(words);
    else {
      setPhase('idle');
      setProblem((p) => p ?? 'noSpeech');
    }
  }

  function start() {
    heard.current = '';
    setText('');
    setError(null);
    setProblem(null);
    setShown(true);
    const s: Session = { stopping: false, sent: false, timer: null, fallback: null, prior: '', emptyEnds: 0 };
    session.current = s;
    // a safety net only: two minutes of listening sends what was heard
    s.timer = setTimeout(() => send(s), MAX_LISTEN_MS);
    const pause = () => {
      // the phone will not listen again without a tap: the words wait for the tap that sends them
      if (heard.current.trim()) setPhase('paused');
      else send(s);
    };
    const begin = () => {
      const r = makeRecognizer();
      if (!r) return pause();
      recognizer.current = r;
      r.lang = locale === 'es' ? 'es-MX' : 'en-US';
      r.interimResults = true;
      r.continuous = true;
      let heardHere = false;
      r.onresult = (e) => {
        if (s.sent) return;
        heardHere = true;
        let finals = '';
        let interim = '';
        for (let i = 0; i < e.results.length; i += 1) {
          const piece = e.results[i]!;
          const said = piece[0].transcript.trim();
          if (!said) continue;
          if (piece.isFinal) {
            // some phones repeat what they already gave: keep it once
            if (finals.endsWith(said)) continue;
            finals = said.startsWith(finals) && finals ? said : `${finals} ${said}`.trim();
          } else interim = `${interim} ${said}`.trim();
        }
        heard.current = `${s.prior} ${finals} ${interim}`.replace(/\s+/g, ' ').trim();
        setText(heard.current);
      };
      r.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed' || e.error === 'audio-capture') {
          // blocked before a word: say so; blocked on a restart: the words wait for the tap
          if (!heard.current.trim()) {
            end(s);
            setPhase('idle');
            setProblem('micBlocked');
          } else s.emptyEnds = EMPTY_ENDS;
        }
      };
      r.onend = () => {
        if (s.sent) return;
        // everything heard so far, the words not yet final included
        s.prior = heard.current;
        if (s.stopping) return send(s);
        // the browser ends its listening at a pause on its own: listen again, keeping what was heard
        s.emptyEnds = heardHere ? 0 : s.emptyEnds + 1;
        if (s.emptyEnds >= EMPTY_ENDS) return pause();
        try {
          begin();
        } catch {
          pause();
        }
      };
      r.start();
    };
    setPhase('listening');
    try {
      begin();
    } catch {
      end(s);
      setPhase('idle');
      setProblem('micBlocked');
    }
  }

  function press() {
    const s = session.current;
    if (phase === 'busy') return;
    if (phase === 'paused' && s) return send(s);
    if (phase === 'listening' && s) {
      // the second tap is "I'm done": the only thing that sends
      s.stopping = true;
      try {
        recognizer.current?.stop();
      } catch {
        // already stopped
      }
      // a phone that never says it stopped (a call, the app put away) still sends
      s.fallback = setTimeout(() => send(s), STOP_FALLBACK_MS);
      return;
    }
    start();
  }

  const listening = phase === 'listening';
  const paused = phase === 'paused';
  const busy = phase === 'busy';
  const last = [...turns].reverse().find((x) => x.role === 'assistant');
  const label = listening ? t(place === 'tab' ? 'claudeListeningShort' : 'claudeListening') : paused ? t('claudeTapToSend') : busy ? t('claudeWorking') : t('claudeTitle');
  // the words: live while talking, then what was sent, until the next tap
  const words = listening || paused ? text : lastHeard;
  const answer = listening || paused || busy ? null : problem ? t(problem === 'micBlocked' ? 'claudeMicBlocked' : 'claudeNoSpeech') : error ? errorText(error) : (last?.text ?? null);
  const show = listening || paused || (shown && !!(words || answer));
  return (
    <div className={`claude-slot claude-${place}`}>
      <button type="button" className={`${place === 'side' ? 'sidelink' : 'tab'} claude-link${listening || paused ? ' on' : ''}`} onClick={press} aria-pressed={listening || paused} aria-label={t('claudeTalk')} aria-busy={busy}>
        <Diamond filled={listening || paused || busy} />
        <span>{label}</span>
      </button>
      {show ? (
        <div className="claude-reply" role="status" aria-live="polite">
          {!listening && !paused && !busy ? (
            <button type="button" className="claude-close" aria-label={t('close')} onClick={() => setShown(false)}>
              ✕
            </button>
          ) : null}
          {listening && !words ? <p className="claude-heard">{t('claudeSayIt')}</p> : null}
          {paused ? <p className="claude-heard">{t('claudePausedHint')}</p> : null}
          {words ? (
            <p className="claude-heard">
              <span className="claude-you">{t('claudeYou')}</span> “{words}”
            </p>
          ) : null}
          {listening && words && place === 'tab' ? <p className="claude-heard">{t('claudeTapWhenDone')}</p> : null}
          {answer ? <p className={error || problem ? 'claude-answer bad' : 'claude-answer'}>{answer}</p> : null}
          {answer && !error && !problem && used.length ? <p className="claude-used">{used.join(' · ')}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

type Session = { stopping: boolean; sent: boolean; timer: ReturnType<typeof setTimeout> | null; fallback: ReturnType<typeof setTimeout> | null; prior: string; emptyEnds: number };

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function makeRecognizerAvailable(): boolean {
  const w = window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
  return !!(w.SpeechRecognition ?? w.webkitSpeechRecognition);
}
